import { Button, Card, TextInput } from "@tremor/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { createApiKey, deleteApiKey } from "../api/api_keys.js";
import { updateMyNotificationPreferences } from "../api/channels.js";
import {
  disableTOTP,
  enableTOTP,
  regenerateRecoveryCodes,
  setEmailOTP,
  startTOTPSetup,
  updateMe,
  updateMyPassword,
} from "../api/me.js";
import { useApiKeys, useMe } from "../queries/misc.js";
import { useMyNotificationPreferences } from "../queries/channels.js";
import { useQueryClient } from "@tanstack/react-query";
import { keys as qkeys } from "../queryKeys.js";
import { useAuthStore } from "../store/authStore.js";
import PasskeysSection from "../components/PasskeysSection.jsx";
import { fmtDate } from "../utils/format.js";

function Section({ title, description, children }) {
  return (
    <Card className="p-0">
      <div className="border-b border-gray-100 px-5 py-4">
        <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
        {description && <p className="mt-1 text-xs text-gray-500">{description}</p>}
      </div>
      <div className="space-y-4 px-5 py-4">{children}</div>
    </Card>
  );
}

export default function User() {
  const { t, i18n } = useTranslation(["user", "common"]);
  const storeUser = useAuthStore((s) => s.user);
  const storeWorkspace = useAuthStore((s) => s.workspace);
  const setIdentity = useAuthStore((s) => s.setIdentity);

  const [fullName, setFullName] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
  });
  const [totpSetup, setTotpSetup] = useState(null);
  const [totpCode, setTotpCode] = useState("");
  const [totpDisableCode, setTotpDisableCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  // Notification preferences form (server data is the source of truth, but
  // we keep an editable copy in local state until the user clicks Save).
  const [notifForm, setNotifForm] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);

  // API key creation form state
  const [newKeyName, setNewKeyName] = useState("");
  const [creatingKey, setCreatingKey] = useState(false);
  const [newKeySecret, setNewKeySecret] = useState(null);

  const qc = useQueryClient();
  const meQuery = useMe();
  const apiKeysQuery = useApiKeys();
  const notifPrefsQuery = useMyNotificationPreferences();
  const profile = meQuery.data;
  const apiKeys = apiKeysQuery.data;
  const notifPrefs = notifPrefsQuery.data;
  const loading =
    meQuery.isPending || apiKeysQuery.isPending || notifPrefsQuery.isPending;

  // Sync the form fields whenever the cached server data changes.
  useEffect(() => {
    if (profile) setFullName(profile.full_name || "");
  }, [profile?.full_name]);

  useEffect(() => {
    if (notifPrefs) {
      setNotifForm({
        email_enabled: notifPrefs.email_enabled ?? true,
        slack_dm_enabled: notifPrefs.slack_dm_enabled ?? false,
        slack_user_id: notifPrefs.slack_user_id ?? "",
        phone_number: notifPrefs.phone_number ?? "",
        sms_enabled: notifPrefs.sms_enabled ?? false,
        voice_enabled: notifPrefs.voice_enabled ?? false,
        quiet_hours_enabled: notifPrefs.quiet_hours_enabled ?? false,
        quiet_hours_start: notifPrefs.quiet_hours_start ?? "22:00",
        quiet_hours_end: notifPrefs.quiet_hours_end ?? "07:00",
        quiet_hours_timezone:
          notifPrefs.quiet_hours_timezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone ||
          "UTC",
      });
    }
  }, [notifPrefs]);

  async function handleProfileSubmit(e) {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const updated = await updateMe({ full_name: fullName || null });
      qc.invalidateQueries({ queryKey: qkeys.me.current });
      setIdentity(updated, storeWorkspace);
      toast.success(t("profile.success"));
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Could not update profile");
    } finally {
      setSavingProfile(false);
    }
  }

  async function handlePasswordSubmit(e) {
    e.preventDefault();
    setChangingPassword(true);
    try {
      await updateMyPassword(passwordForm);
      setPasswordForm({ current_password: "", new_password: "" });
      toast.success(t("password.success"));
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Could not update password");
    } finally {
      setChangingPassword(false);
    }
  }

  async function handleEmailOTPToggle(enabled) {
    try {
      const updated = await setEmailOTP(enabled);
      qc.invalidateQueries({ queryKey: qkeys.me.current });
      setIdentity(updated, storeWorkspace);
      toast.success(enabled ? t("email_otp.success_enabled") : t("email_otp.success_disabled"));
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Could not update email OTP");
    }
  }

  async function handleStartTOTP() {
    try {
      const data = await startTOTPSetup();
      setTotpSetup(data);
      setTotpCode("");
      toast.success(t("authenticator.success_ready"));
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Could not start TOTP setup");
    }
  }

  async function handleEnableTOTP(e) {
    e.preventDefault();
    try {
      const data = await enableTOTP({ secret: totpSetup.secret, code: totpCode });
      qc.invalidateQueries({ queryKey: qkeys.me.current });
      setIdentity(data, storeWorkspace);
      setRecoveryCodes(data.recovery_codes || []);
      setTotpSetup(null);
      setTotpCode("");
      toast.success(t("authenticator.success_enabled"));
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Could not enable TOTP");
    }
  }

  async function handleDisableTOTP(e) {
    e.preventDefault();
    try {
      await disableTOTP({ code: totpDisableCode });
      qc.invalidateQueries({ queryKey: qkeys.me.current });
      setTotpDisableCode("");
      setRecoveryCodes([]);
      toast.success(t("authenticator.success_disabled"));
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Could not disable TOTP");
    }
  }

  async function handleRegenerateRecoveryCodes() {
    try {
      const data = await regenerateRecoveryCodes();
      setRecoveryCodes(data.recovery_codes || []);
      qc.invalidateQueries({ queryKey: qkeys.me.current });
      toast.success(t("authenticator.success_regenerated"));
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Could not regenerate recovery codes");
    }
  }

  async function handleSaveNotifPrefs(e) {
    e.preventDefault();
    setSavingPrefs(true);
    try {
      await updateMyNotificationPreferences({
        email_enabled: notifForm.email_enabled,
        slack_dm_enabled: notifForm.slack_dm_enabled,
        slack_user_id: notifForm.slack_user_id.trim() || null,
        phone_number: notifForm.phone_number.trim() || null,
        sms_enabled: notifForm.sms_enabled,
        voice_enabled: notifForm.voice_enabled,
        quiet_hours_enabled: notifForm.quiet_hours_enabled,
        quiet_hours_start: notifForm.quiet_hours_start,
        quiet_hours_end: notifForm.quiet_hours_end,
        quiet_hours_timezone: notifForm.quiet_hours_timezone,
      });
      qc.invalidateQueries({ queryKey: ["my-notification-preferences"] });
      toast.success(t("notifications.success"));
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Could not save preferences");
    } finally {
      setSavingPrefs(false);
    }
  }

  async function handleCreateApiKey(e) {
    e.preventDefault();
    if (!newKeyName.trim()) return;
    setCreatingKey(true);
    try {
      const created = await createApiKey(newKeyName.trim());
      qc.invalidateQueries({ queryKey: qkeys.apiKeys.all });
      setNewKeySecret(created.key);
      setNewKeyName("");
      toast.success(t("api_keys.success_created"));
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Could not create API key");
    } finally {
      setCreatingKey(false);
    }
  }

  async function handleDeleteApiKey(id) {
    try {
      await deleteApiKey(id);
      qc.invalidateQueries({ queryKey: qkeys.apiKeys.all });
      toast.success(t("api_keys.success_revoked"));
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Could not revoke API key");
    }
  }

  function changeLanguage(lng) {
    i18n.changeLanguage(lng);
    toast.success("Language updated");
  }

  if (loading || !profile) {
    return <p className="text-sm text-gray-500">{t("common:loading")}</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
        <p className="mt-1 text-sm text-gray-500">{t("subtitle")}</p>
      </div>

      <Section title={t("profile.title")} description={t("profile.description")}>
        <form onSubmit={handleProfileSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("profile.email")}</label>
            <TextInput disabled value={profile.email} className="mt-1" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("profile.full_name")}</label>
            <TextInput
              value={fullName}
              onValueChange={setFullName}
              className="mt-1"
            />
          </div>
          <Button type="submit" disabled={savingProfile}>
            {savingProfile ? t("profile.saving") : t("profile.save")}
          </Button>
        </form>
      </Section>

      <Section title={t("language.title")} description={t("language.description")}>
        <div className="max-w-xs">
          <label className="mb-1 block text-xs font-medium text-gray-600">{t("language.select")}</label>
          <select
            value={i18n.language.split("-")[0]}
            onChange={(e) => changeLanguage(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          >
            <option value="en">{t("language.en")}</option>
            <option value="de">{t("language.de")}</option>
          </select>
        </div>
      </Section>

      <Section title={t("password.title")} description={t("password.description")}>
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("password.current")}</label>
            <TextInput
              type="password"
              value={passwordForm.current_password}
              onValueChange={(v) => setPasswordForm((prev) => ({ ...prev, current_password: v }))}
              className="mt-1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("password.new")}</label>
            <TextInput
              type="password"
              value={passwordForm.new_password}
              onValueChange={(v) => setPasswordForm((prev) => ({ ...prev, new_password: v }))}
              className="mt-1"
            />
          </div>
          <Button type="submit" disabled={changingPassword}>
            {changingPassword ? t("password.updating") : t("password.update")}
          </Button>
        </form>
      </Section>

      <PasskeysSection />

      <Section title={t("email_otp.title")} description={t("email_otp.description")}>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-gray-800">
              {profile.email_otp_enabled ? t("email_otp.enabled") : t("email_otp.disabled")}
            </p>
            <p className="text-xs text-gray-500">{t("email_otp.info")}</p>
          </div>
          <Button
            variant="secondary"
            onClick={() => handleEmailOTPToggle(!profile.email_otp_enabled)}
          >
            {profile.email_otp_enabled ? t("email_otp.disable") : t("email_otp.enable")}
          </Button>
        </div>
      </Section>

      <Section title={t("authenticator.title")} description={t("authenticator.description")}>
        {!profile.totp_enabled ? (
          <div className="space-y-4">
            <Button onClick={handleStartTOTP}>{t("authenticator.start")}</Button>

            {totpSetup && (
              <form onSubmit={handleEnableTOTP} className="space-y-4 rounded-md border border-gray-200 bg-gray-50 p-4">
                <div>
                  <p className="text-sm font-medium text-gray-800">{t("authenticator.secret")}</p>
                  <code className="mt-1 block rounded bg-white px-3 py-2 text-sm text-gray-700">
                    {totpSetup.secret}
                  </code>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">{t("authenticator.uri")}</p>
                  <code className="mt-1 block break-all rounded bg-white px-3 py-2 text-xs text-gray-700">
                    {totpSetup.otpauth_uri}
                  </code>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t("authenticator.code")}</label>
                  <TextInput
                    value={totpCode}
                    onValueChange={setTotpCode}
                    placeholder="123456"
                    className="mt-1"
                  />
                </div>
                <Button type="submit">{t("authenticator.enable")}</Button>
              </form>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              {t("authenticator.enabled_info", { count: profile.recovery_codes_remaining })}
            </div>
            <form onSubmit={handleDisableTOTP} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t("authenticator.current_code")}</label>
                <TextInput
                  value={totpDisableCode}
                  onValueChange={setTotpDisableCode}
                  placeholder="123456"
                  className="mt-1"
                />
              </div>
              <div className="flex gap-3">
                <Button type="submit" variant="secondary" color="red">
                  {t("email_otp.disable")}
                </Button>
                <Button type="button" variant="secondary" onClick={handleRegenerateRecoveryCodes}>
                  {t("authenticator.regenerate")}
                </Button>
              </div>
            </form>
          </div>
        )}

        {recoveryCodes.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-medium text-amber-800">{t("authenticator.save_recovery")}</p>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-amber-900">
              {recoveryCodes.map((code) => (
                <code key={code} className="rounded bg-white px-3 py-2">
                  {code}
                </code>
              ))}
            </div>
          </div>
        )}
      </Section>

      <Section
        title={t("notifications.title")}
        description={t("notifications.description")}
      >
        {notifForm && (
          <form onSubmit={handleSaveNotifPrefs} className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-800">{t("notifications.email")}</p>
                <p className="text-xs text-gray-500">{t("notifications.email_info")}</p>
              </div>
              <input
                type="checkbox"
                checked={notifForm.email_enabled}
                onChange={(e) => setNotifForm((prev) => ({ ...prev, email_enabled: e.target.checked }))}
                className="h-4 w-4 rounded border-gray-300 text-teal-600"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-800">{t("notifications.slack")}</p>
                <p className="text-xs text-gray-500">{t("notifications.slack_info")}</p>
              </div>
              <input
                type="checkbox"
                checked={notifForm.slack_dm_enabled}
                onChange={(e) => setNotifForm((prev) => ({ ...prev, slack_dm_enabled: e.target.checked }))}
                className="h-4 w-4 rounded border-gray-300 text-teal-600"
              />
            </div>
            {notifForm.slack_dm_enabled && (
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">{t("notifications.slack_id")}</label>
                <TextInput
                  value={notifForm.slack_user_id}
                  onValueChange={(v) => setNotifForm((prev) => ({ ...prev, slack_user_id: v }))}
                  placeholder="U0123456789"
                />
                <p className="mt-1 text-xs text-gray-400">{t("notifications.slack_id_info")}</p>
              </div>
            )}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">{t("notifications.phone")}</label>
              <TextInput
                value={notifForm.phone_number}
                onValueChange={(v) => setNotifForm((prev) => ({ ...prev, phone_number: v }))}
                placeholder="+1 555 000 0000"
              />
              <p className="mt-1 text-xs text-gray-400">{t("notifications.phone_info")}</p>
            </div>
            {notifForm.phone_number.trim() && (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{t("notifications.sms")}</p>
                    <p className="text-xs text-gray-500">{t("notifications.sms_info")}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifForm.sms_enabled}
                    onChange={(e) => setNotifForm((prev) => ({ ...prev, sms_enabled: e.target.checked }))}
                    className="h-4 w-4 rounded border-gray-300 text-teal-600"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{t("notifications.voice")}</p>
                    <p className="text-xs text-gray-500">{t("notifications.voice_info")}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifForm.voice_enabled}
                    onChange={(e) => setNotifForm((prev) => ({ ...prev, voice_enabled: e.target.checked }))}
                    className="h-4 w-4 rounded border-gray-300 text-teal-600"
                  />
                </div>
                {(notifForm.sms_enabled || notifForm.voice_enabled) && (
                  <div className="rounded-md border border-gray-200 p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{t("notifications.quiet_hours")}</p>
                        <p className="text-xs text-gray-500">{t("notifications.quiet_hours_info")}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={notifForm.quiet_hours_enabled}
                        onChange={(e) => setNotifForm((prev) => ({ ...prev, quiet_hours_enabled: e.target.checked }))}
                        className="h-4 w-4 rounded border-gray-300 text-teal-600"
                      />
                    </div>
                    {notifForm.quiet_hours_enabled && (
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">{t("notifications.quiet_hours_start")}</label>
                          <input
                            type="time"
                            value={notifForm.quiet_hours_start}
                            onChange={(e) => setNotifForm((prev) => ({ ...prev, quiet_hours_start: e.target.value }))}
                            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">{t("notifications.quiet_hours_end")}</label>
                          <input
                            type="time"
                            value={notifForm.quiet_hours_end}
                            onChange={(e) => setNotifForm((prev) => ({ ...prev, quiet_hours_end: e.target.value }))}
                            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="mb-1 block text-xs font-medium text-gray-600">{t("notifications.quiet_hours_timezone")}</label>
                          <TextInput
                            value={notifForm.quiet_hours_timezone}
                            onValueChange={(v) => setNotifForm((prev) => ({ ...prev, quiet_hours_timezone: v }))}
                            placeholder="Europe/London"
                          />
                        </div>
                        <p className="col-span-2 text-xs text-gray-400">{t("notifications.quiet_hours_critical_note")}</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            <Button type="submit" disabled={savingPrefs}>
              {savingPrefs ? t("notifications.saving") : t("notifications.save")}
            </Button>
          </form>
        )}
      </Section>

      <Section
        title={t("api_keys.title")}
        description={t("api_keys.description")}
      >
        {/* New key revealed after creation */}
        {newKeySecret && (
          <div className="rounded-md border border-green-200 bg-green-50 p-4">
            <p className="text-sm font-medium text-green-800">{t("api_keys.new_title")}</p>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 break-all rounded bg-white px-3 py-2 text-sm text-gray-800">
                {newKeySecret}
              </code>
              <Button
                variant="secondary"
                size="xs"
                onClick={() => {
                  navigator.clipboard.writeText(newKeySecret);
                  toast.success(t("api_keys.copied"));
                }}
              >
                {t("api_keys.copy")}
              </Button>
            </div>
            <button
              type="button"
              onClick={() => setNewKeySecret(null)}
              className="mt-3 text-xs text-green-700 underline"
            >
              {t("api_keys.dismiss")}
            </button>
          </div>
        )}

        {/* Create form */}
        <form onSubmit={handleCreateApiKey} className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700">{t("api_keys.name")}</label>
            <TextInput
              value={newKeyName}
              onValueChange={setNewKeyName}
              placeholder={t("api_keys.placeholder")}
              className="mt-1"
            />
          </div>
          <Button type="submit" disabled={creatingKey || !newKeyName.trim()}>
            {creatingKey ? t("api_keys.creating") : t("api_keys.create")}
          </Button>
        </form>

        {/* Key list */}
        {apiKeys === null ? (
          <p className="text-sm text-gray-400">{t("common:loading")}</p>
        ) : apiKeys.length === 0 ? (
          <p className="text-sm text-gray-400">{t("api_keys.no_keys")}</p>
        ) : (
          <div className="divide-y divide-gray-100 rounded-md border border-gray-200">
            {apiKeys.map((k) => (
              <div key={k.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-gray-800">{k.name}</p>
                  <p className="mt-0.5 font-mono text-xs text-gray-400">
                    {k.prefix}&hellip;
                    <span className="ml-3 font-sans text-gray-400">
                      {t("api_keys.created", { date: fmtDate(k.created_at) })}
                      {k.last_used_at && (
                        <> &middot; {t("api_keys.last_used", { date: fmtDate(k.last_used_at) })}</>
                      )}
                    </span>
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="xs"
                  color="red"
                  onClick={() => handleDeleteApiKey(k.id)}
                >
                  {t("api_keys.revoke")}
                </Button>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
